// Blocos reutilizados pelos pedidos da categoria "Pedidos de Sociedade" (contratos e
// alterações de sociedades de advogados). O endereço aqui é mais enxuto que o
// enderecoResidencial de pessoa física (sem complemento/telefone), então tem seu próprio builder.

// `incluirCnpj: false` é usado pelos pedidos de REGISTRO de uma sociedade nova, que ainda
// não tem CNPJ no momento do pedido (o CNPJ só existe depois que o registro é efetivado).
export const identificacaoSociedadeSection = (camposExtras = [], { incluirCnpj = true } = {}) => ({
  title: 'Identificação da Sociedade',
  fields: [
    { type: 'text', name: 'nomeSociedade', label: 'Nome da Sociedade', required: true, placeholder: 'Razão social da sociedade' },
    ...(incluirCnpj ? [{ type: 'cnpj', name: 'cnpj', label: 'CNPJ', required: true }] : []),
    ...camposExtras
  ]
});

// `autoCep: true` (piloto, 2026-08) — ver enderecoSections.js pro mesmo mecanismo
// aplicado ao dialeto de pessoa física; aqui vale igual, sem prefixo nos nomes.
// `incluirPais: true` mostra o País (preenchido pelo CEP), pros modelos que têm
// PaisComercial — ver DADOS_REQUERIMENTO_ENDERECO_SOCIEDADE_COM_PAIS.
const enderecoSociedadeSection = ({ autoCep = false, incluirPais = false, tituloSecao = 'Endereço da Sociedade' } = {}) => ({
  title: tituloSecao,
  fields: [
    {
      type: 'cep', name: 'cep', label: 'CEP', required: true,
      ...(autoCep && {
        enderecoFields: {
          logradouro: 'rua', bairro: 'bairro', municipio: 'municipio', estado: 'estado',
          ...(incluirPais && { pais: 'pais' })
        }
      })
    },
    { type: 'text', name: 'rua', label: 'Rua', required: true, disabled: autoCep },
    { type: 'text', name: 'numero', label: 'Número', required: true },
    { type: 'text', name: 'bairro', label: 'Bairro', required: true, disabled: autoCep },
    ...(incluirPais ? [{ type: 'text', name: 'pais', label: 'País', required: true, defaultValue: 'Brasil', disabled: autoCep }] : []),
    { type: 'text', name: 'estado', label: 'Estado', required: true, disabled: autoCep },
    { type: 'text', name: 'municipio', label: 'Município', required: true, disabled: autoCep }
  ]
});

export const enderecoSociedadeStep = ({ id = 'endereco', title = 'Endereço', autoCep = false, incluirPais = false, tituloSecao } = {}) => ({
  id,
  title,
  sections: [enderecoSociedadeSection({ autoCep, incluirPais, tituloSecao })]
});

// Fragmento de dadosRequerimentoConfig (ver dadosRequerimentoService.js) pro
// endereço enxuto de enderecoSociedadeSection (sem prefixo) — confirmado ao
// vivo contra GET /modelo-requerimento/:id/campos que esse endereço "único" da
// sociedade corresponde ao grupo "Comercial" da API, igual o Profissional de
// pessoa física.
export const DADOS_REQUERIMENTO_ENDERECO_SOCIEDADE = {
  campos: [
    { field: 'rua', campoNome: 'LogradouroComercial' },
    { field: 'numero', campoNome: 'NumeroComercial' },
    { field: 'bairro', campoNome: 'BairroComercial' }
  ],
  enderecos: [
    { cepField: 'cep', campoNomeCep: 'CEPComercial', campoNomeEstado: 'EstadoComercial', campoNomeMunicipio: 'MunicipioComercial' }
  ]
};

// Mesmo endereço, pros modelos que também pedem PaisComercial (ex: 328, Abertura de
// Filial) — o ID do país vem do mesmo CEP, junto com Estado/Município.
export const DADOS_REQUERIMENTO_ENDERECO_SOCIEDADE_COM_PAIS = {
  ...DADOS_REQUERIMENTO_ENDERECO_SOCIEDADE,
  enderecos: DADOS_REQUERIMENTO_ENDERECO_SOCIEDADE.enderecos.map(e => ({ ...e, campoNomePais: 'PaisComercial' }))
};

// Usado pelos pedidos de registro de sociedade (unipessoal/pluripessoal), onde tanto o
// endereço residencial do advogado quanto o endereço profissional da sociedade são
// obrigatórios ao mesmo tempo — por isso os nomes de campo levam sufixo, para não colidir.

const enderecoResidencialAdvogadoSection = ({ autoCep = false } = {}) => ({
  title: 'Endereço Residencial do Advogado',
  fields: [
    {
      type: 'cep', name: 'cepResidencial', label: 'CEP', required: true,
      ...(autoCep && {
        enderecoFields: {
          logradouro: 'ruaResidencial', bairro: 'bairroResidencial',
          municipio: 'municipioResidencial', estado: 'estadoResidencial'
        }
      })
    },
    { type: 'text', name: 'ruaResidencial', label: 'Rua', required: true, disabled: autoCep },
    { type: 'text', name: 'numeroResidencial', label: 'Número', required: true },
    { type: 'text', name: 'bairroResidencial', label: 'Bairro', required: true, disabled: autoCep },
    { type: 'text', name: 'estadoResidencial', label: 'Estado', required: true, disabled: autoCep },
    { type: 'text', name: 'municipioResidencial', label: 'Município', required: true, disabled: autoCep },
    { type: 'email', name: 'emailResidencial', label: 'E-mail', required: true, placeholder: 'email@exemplo.com' }
  ]
});

const enderecoProfissionalSociedadeSection = ({ autoCep = false } = {}) => ({
  title: 'Endereço Profissional da Sociedade',
  fields: [
    {
      type: 'cep', name: 'cepProfissional', label: 'CEP', required: true,
      ...(autoCep && {
        enderecoFields: {
          logradouro: 'ruaProfissional', bairro: 'bairroProfissional',
          municipio: 'municipioProfissional', estado: 'estadoProfissional', pais: 'paisProfissional'
        }
      })
    },
    { type: 'text', name: 'ruaProfissional', label: 'Rua', required: true, disabled: autoCep },
    { type: 'text', name: 'numeroProfissional', label: 'Número', required: true },
    { type: 'text', name: 'complementoProfissional', label: 'Complemento' },
    { type: 'text', name: 'bairroProfissional', label: 'Bairro', required: true, disabled: autoCep },
    { type: 'text', name: 'paisProfissional', label: 'País', required: true, defaultValue: 'Brasil', disabled: autoCep },
    { type: 'text', name: 'estadoProfissional', label: 'Estado', required: true, disabled: autoCep },
    { type: 'text', name: 'municipioProfissional', label: 'Município', required: true, disabled: autoCep },
    { type: 'email', name: 'emailProfissional', label: 'E-mail', required: true, placeholder: 'email@exemplo.com' },
    { type: 'phone', name: 'celularProfissional', label: 'Celular', required: true },
    { type: 'phone', name: 'telefoneProfissional', label: 'Telefone' },
    {
      type: 'radio',
      name: 'enderecoCorrespondencia',
      label: 'Endereço para receber as correspondências',
      required: true,
      options: [
        { value: 'PROFISSIONAL', label: 'Endereço Profissional da Sociedade' }
      ],
      defaultValue: 'PROFISSIONAL'
    }
  ]
});

export const enderecoAdvogadoESociedadeStep = ({ id = 'endereco', title = 'Endereço', autoCep = false } = {}) => ({
  id,
  title,
  sections: [
    enderecoResidencialAdvogadoSection({ autoCep }),
    enderecoProfissionalSociedadeSection({ autoCep })
  ]
});

// Fragmentos de dadosRequerimentoConfig pros 2 endereços de
// enderecoAdvogadoESociedadeStep — residencial do advogado (grupo "Residencial"
// da API) e profissional da sociedade (grupo "Comercial"), confirmados ao vivo.
export const DADOS_REQUERIMENTO_ENDERECO_RESIDENCIAL_ADVOGADO = {
  campos: [
    { field: 'ruaResidencial', campoNome: 'LogradouroResidencial' },
    { field: 'numeroResidencial', campoNome: 'NumeroResidencial' },
    { field: 'bairroResidencial', campoNome: 'BairroResidencial' },
    { field: 'emailResidencial', campoNome: 'EmailResidencial' }
  ],
  enderecos: [
    { cepField: 'cepResidencial', campoNomeCep: 'CEPResidencial', campoNomeEstado: 'EstadoResidencial', campoNomeMunicipio: 'MunicipioResidencial' }
  ]
};

export const DADOS_REQUERIMENTO_ENDERECO_PROFISSIONAL_SOCIEDADE = {
  campos: [
    { field: 'ruaProfissional', campoNome: 'LogradouroComercial' },
    { field: 'numeroProfissional', campoNome: 'NumeroComercial' },
    { field: 'complementoProfissional', campoNome: 'ComplementoComercial' },
    { field: 'bairroProfissional', campoNome: 'BairroComercial' },
    { field: 'emailProfissional', campoNome: 'EmailComercial' },
    { field: 'celularProfissional', campoNome: 'Telefone2Comercial' },
    { field: 'telefoneProfissional', campoNome: 'TelefoneComercial' }
  ],
  enderecos: [
    { cepField: 'cepProfissional', campoNomeCep: 'CEPComercial', campoNomeEstado: 'EstadoComercial', campoNomeMunicipio: 'MunicipioComercial' }
  ]
};
